/**
 * Fill the ARCaml Customer Services Agreement PDF with party details.
 *
 * Uses pdf-lib to overlay text on the static PDF template, replacing
 * the placeholder fields on pages 1 (party details), 12 (execution block),
 * and 13 (Schedule 1).
 *
 * Returns a blob URL that can be used as an iframe src.
 */

import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getDraftDocumentUrl } from '../services/agreementService';

export interface AgreementPdfData {
  partyName: string;        // e.g. "IUV Pty. Ltd."
  partyAbn?: string;        // e.g. "12 345 678 901"
  partyAcn?: string;        // e.g. "671 781 589"
  partyAddress?: string;    // e.g. "123 Main St, Sydney NSW 2000"
  partyEmail?: string;      // e.g. "admin@iuv.com.au"
  agreementDate?: string;   // e.g. "5 March 2026"
  signatoryName?: string;   // e.g. "John Smith" — typed signature from ReviewSign step
  drawnSignature?: string;  // base64 data URL from canvas.toDataURL()
  signatureMode?: 'typed' | 'drawn' | 'upload';
  // Acknowledgement checkboxes (page 12)
  ackTermsOfUse?: boolean;
  ackPrivacyPolicy?: boolean;
  ackServicesAgreement?: boolean;
  ackAuthorisedToSign?: boolean;
}

/**
 * Load the template PDF from the public folder, overlay the party
 * details, and return a blob URL for the filled PDF.
 */
export async function fillAgreementPdf(data: AgreementPdfData): Promise<string> {
  // 1. Fetch the template PDF — from S3 drafts/ prefix only
  const pdfUrl = await getDraftDocumentUrl('agreement.pdf');
  if (!pdfUrl) {
    throw new Error('Agreement PDF template not found in S3 drafts/');
  }
  const response = await fetch(pdfUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch PDF template: ${response.status}`);
  }
  const templateBytes = await response.arrayBuffer();
  // Validate it's actually a PDF (first 4 bytes should be %PDF)
  const magic = new Uint8Array(templateBytes.slice(0, 4));
  if (String.fromCharCode(...magic) !== '%PDF') {
    throw new Error('Fetched file is not a valid PDF');
  }

  // 2. Load into pdf-lib
  const pdfDoc = await PDFDocument.load(templateBytes);
  const pages = pdfDoc.getPages();
  const page1 = pages[0];

  // Embed fonts
  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  // Colours
  const textColor = rgb(0.1, 0.1, 0.1);
  const whiteColor = rgb(1, 1, 1);
  const linkColor = rgb(0.0, 0.3, 0.7);

  // Precomputed labels
  const acnAbnLabel = data.partyAcn
    ? `ACN ${data.partyAcn}`
    : data.partyAbn
      ? `ABN ${data.partyAbn}`
      : '';

  // Helper: embed drawn signature image once (reusable across pages)
  let sigImage: Awaited<ReturnType<typeof pdfDoc.embedPng>> | null = null;
  if (data.signatureMode === 'drawn' && data.drawnSignature) {
    try {
      const base64 = data.drawnSignature.split(',')[1];
      if (base64) {
        const sigBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        sigImage = await pdfDoc.embedPng(sigBytes);
      }
    } catch (sigErr) {
    }
  }

  // Helper: render typed signature as a cursive image using canvas
  const renderTypedSignatureAsImage = async (name: string): Promise<Awaited<ReturnType<typeof pdfDoc.embedPng>> | null> => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const fontSize = 48;
      const fontFamily = "'Brush Script MT', 'Segoe Script', cursive";
      ctx.font = `${fontSize}px ${fontFamily}`;
      const metrics = ctx.measureText(name);
      const textWidth = metrics.width;
      const textHeight = fontSize * 1.4;

      canvas.width = Math.ceil(textWidth + 20);
      canvas.height = Math.ceil(textHeight + 10);

      // Clear with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw the text
      ctx.font = `${fontSize}px ${fontFamily}`;
      ctx.fillStyle = '#0D2647';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, 10, canvas.height / 2);

      // Convert to PNG
      const dataUrl = canvas.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];
      if (!base64) return null;
      const sigBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      return await pdfDoc.embedPng(sigBytes);
    } catch {
      return null;
    }
  };

  // If typed mode and no drawn image, render the typed name as a cursive image
  if (!sigImage && data.signatureMode === 'typed' && data.signatoryName) {
    const typedImage = await renderTypedSignatureAsImage(data.signatoryName);
    if (typedImage) {
      sigImage = typedImage;
    }
  }

  // Helper: draw signature (image or typed) at a given position on a page
  const drawSignature = (
    targetPage: typeof page1,
    x: number,
    y: number,
    maxW = 130,
    imgH = 30,
  ) => {
    if (sigImage) {
      const sigAspect = sigImage.width / sigImage.height;
      const sigW = Math.min(imgH * sigAspect, maxW);
      targetPage.drawImage(sigImage, { x, y, width: sigW, height: imgH });
    } else if (data.signatoryName) {
      // Fallback: plain italic text if canvas rendering failed
      targetPage.drawText(data.signatoryName, {
        x, y: y + 4, size: 14, font: fontItalic, color: rgb(0.05, 0.15, 0.4),
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PAGE 1 (pages[0]): Principal (Reporting Entity) column
  // ═══════════════════════════════════════════════════════════════
  // Exact line-level coordinates from pdfminer:
  //   "PRINCIPAL (REPORTING ENTITY)"   LINE x0=297.8 y0=483.2 x1=450.7 y1=492.7   ← KEEP
  //   "{{principal.firm_name}}"         LINE x0=297.8 y0=469.3 x1=393.3 y1=478.8
  //   "ACN/ABN:  {{principal.acn_abn}}" LINE x0=297.8 y0=458.0 x1=427.7 y1=467.0
  //   "{{principal.address}}"           LINE x0=297.8 y0=446.6 x1=377.8 y1=455.6
  //   "{{principal.email}}"             LINE x0=297.8 y0=435.3 x1=367.2 y1=444.3
  //   "Date of Agreement:  {{execution_date}}"  LINE x0=72.1 y0=399.9 x1=236.7 y1=409.4
  const p1x = 297.8;
  const p1FontSize = 9;

  // Address wraps to max 2 lines (60-char input limit, 30 chars/line)
  const p1AddrLines = data.partyAddress ? splitText(data.partyAddress, 30).slice(0, 2) : [];
  const p1ExtraAddrLines = Math.max(0, p1AddrLines.length - 1);
  const p1LineSpacing = 11;

  // White-out placeholders with generous coverage (address + email region expands downward)
  page1.drawRectangle({ x: p1x - 1, y: 468, width: 170, height: 12, color: whiteColor }); // firm_name
  page1.drawRectangle({ x: p1x - 1, y: 456, width: 170, height: 12, color: whiteColor }); // acn_abn
  page1.drawRectangle({                                                                      // address + email (dynamic)
    x: p1x - 1,
    y: 434 - (p1ExtraAddrLines * p1LineSpacing),
    width: 170,
    height: 12 + p1LineSpacing + (p1ExtraAddrLines * p1LineSpacing),
    color: whiteColor,
  });
  page1.drawRectangle({ x: 72, y: 398, width: 170, height: 13, color: whiteColor });       // date line

  // Draw replacement text
  page1.drawText(data.partyName || '', {
    x: p1x, y: 470, size: p1FontSize + 1, font: fontBold, color: textColor,
  });

  if (acnAbnLabel) {
    page1.drawText(`ACN/ABN:  ${acnAbnLabel.replace('ACN ', '').replace('ABN ', '')}`, {
      x: p1x, y: 459, size: p1FontSize, font: fontRegular, color: textColor,
    });
  }

  p1AddrLines.forEach((line, i) => {
    page1.drawText(line, {
      x: p1x, y: 447.5 - (i * p1LineSpacing), size: p1FontSize, font: fontRegular, color: textColor,
    });
  });

  if (data.partyEmail) {
    page1.drawText(data.partyEmail, {
      x: p1x, y: 436 - (p1ExtraAddrLines * p1LineSpacing), size: p1FontSize, font: fontRegular, color: linkColor,
    });
  }

  if (data.agreementDate) {
    page1.drawText(`Date of Agreement:  ${data.agreementDate}`, {
      x: 72.1, y: 401, size: p1FontSize, font: fontRegular, color: textColor,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // PAGE 12 (pages[11]): Execution page
  // ═══════════════════════════════════════════════════════════════
  // Right column (Principal's signing block):
  //   "[Firm Name]"                       LINE x0=383.1 y0=408.3 x1=432.7 y1=416.8
  //   "ACN/ABN [xxxxxxxxx]"               LINE x0=383.1 y0=396.5 x1=472.8 y1=405.0
  //   "{{signature_principal}}"            LINE x0=383.1 y0=373.2 x1=465.7 y1=381.7
  //   "______________________________"    LINE x0=383.1 y0=361.4 x1=524.9 y1=369.9
  //   "Director / Authorised Signatory"   LINE x0=383.1 y0=348.5 x1=523.9 y1=358.0
  //   "Name _________________"             LINE x0=383.6 y0=324.8 (v2.0: combined on one line)
  //   "Title:___________________"          LINE x0=383.6 y0=301.0 (v2.0: combined on one line)
  //   "Date:  {{execution_date}}"          LINE x0=383.6 y0=289.6
  // Bottom note: "{{execution_date}} is auto-populated..."  LINE x0=70.5 y0=222.7 x1=449.7 y1=231.2
  // Left col dates:
  //   "Date:  {{execution_date}}"         LINE x0=71.1 y0=324.8 x1=176.1 y1=334.3 (Justin)
  //   "Date:  {{execution_date}}"         LINE x0=227.1 y0=324.8 x1=332.1 y1=334.3 (Eric)

  const page12 = pages[11];
  const exFs = 8.5;

  // -- Right column header: "[Firm Name]" + "ACN/ABN [xxxxxxxxx]" --
  page12.drawRectangle({ x: 382, y: 407, width: 145, height: 11, color: whiteColor });
  page12.drawRectangle({ x: 382, y: 395, width: 145, height: 11, color: whiteColor });
  page12.drawText(data.partyName || '', {
    x: 383.1, y: 409, size: exFs, font: fontBold, color: textColor,
  });
  if (acnAbnLabel) {
    page12.drawText(acnAbnLabel, {
      x: 383.1, y: 397.5, size: exFs, font: fontRegular, color: textColor,
    });
  }

  // -- "{{signature_principal}}" → white-out and draw signature --
  // Signature box: y=365.5 (underline) → y=395 (ACN white-out bottom) ≈ 29px available
  // Keep top of image below y=393 to avoid clipping ACN text at y=396.5
  page12.drawRectangle({ x: 382, y: 367, width: 145, height: 18, color: whiteColor });
  drawSignature(page12, 388, 367, 130, 22);

  // -- "Name _________________" (v2.0: name + underline on one line) --
  page12.drawRectangle({ x: 382, y: 323, width: 145, height: 13, color: whiteColor });
  if (data.signatoryName) {
    page12.drawText(data.signatoryName, {
      x: 383.1, y: 325, size: exFs, font: fontBold, color: textColor,
    });
  }

  // -- "Title:___________________" (v2.0: label + underline on one line) --
  page12.drawRectangle({ x: 382, y: 299, width: 145, height: 13, color: whiteColor });
  page12.drawText('Title:  Director', {
    x: 383.1, y: 301, size: exFs, font: fontBold, color: textColor,
  });

  // -- "Date:  {{execution_date}}" (Principal column) --
  page12.drawRectangle({ x: 382, y: 288, width: 145, height: 12, color: whiteColor });
  page12.drawText(`Date:  ${data.agreementDate || ''}`, {
    x: 383.1, y: 290, size: exFs, font: fontRegular, color: textColor,
  });

  // -- iDeed director dates: "Date:  {{execution_date}}" in left/middle columns --
  // Justin Amos column (gap to Eric: 156pt → safe up to width=150)
  page12.drawRectangle({ x: 70, y: 323, width: 150, height: 13, color: whiteColor });
  page12.drawText(`Date:  ${data.agreementDate || ''}`, {
    x: 71.1, y: 326, size: exFs, font: fontRegular, color: textColor,
  });
  // Eric Chan column (gap to Principal: 156pt → safe up to width=150)
  page12.drawRectangle({ x: 226, y: 323, width: 150, height: 13, color: whiteColor });
  page12.drawText(`Date:  ${data.agreementDate || ''}`, {
    x: 227.1, y: 326, size: exFs, font: fontRegular, color: textColor,
  });

  // -- Principal Acknowledgement checkboxes --
  // 4 checkboxes (☐) on page 12:
  //   ☐ LINE x0=72.1 y0=495.7 y1=505.2 → Terms of Use
  //   ☐ LINE x0=72.1 y0=483.3 y1=492.8 → Privacy Policy
  //   ☐ LINE x0=72.1 y0=470.9 y1=480.4 → Services Agreement
  //   ☐ LINE x0=72.1 y0=458.5 y1=468.0 → Authorised to sign
  const checkboxes = [
    { y: 495.7, checked: data.ackTermsOfUse },
    { y: 483.3, checked: data.ackPrivacyPolicy },
    { y: 470.9, checked: data.ackServicesAgreement },
    { y: 458.5, checked: data.ackAuthorisedToSign },
  ];
  for (const cb of checkboxes) {
    if (cb.checked) {
      // White-out the empty ☐
      page12.drawRectangle({ x: 71, y: cb.y - 1, width: 11, height: 12, color: whiteColor });

      // Draw a filled checkbox: dark border square + checkmark via SVG-style lines
      const bx = 72;
      const by = cb.y;
      const sz = 9;
      const navy = rgb(0.06, 0.15, 0.28);

      // Outer square border
      page12.drawRectangle({ x: bx, y: by, width: sz, height: sz, borderColor: navy, borderWidth: 1.2, color: rgb(0.94, 0.97, 1) });

      // Draw a checkmark using two short thick lines
      // Short leg: bottom-left to bottom-center
      page12.drawLine({ start: { x: bx + 1.5, y: by + 4.5 }, end: { x: bx + 3.5, y: by + 2 }, thickness: 1.8, color: navy });
      // Long leg: bottom-center to top-right
      page12.drawLine({ start: { x: bx + 3.5, y: by + 2 }, end: { x: bx + 7.5, y: by + 7.5 }, thickness: 1.8, color: navy });
    }
  }

  // -- Bottom note: "{{execution_date}} is auto-populated..." --
  page12.drawRectangle({ x: 69, y: 221, width: 455, height: 12, color: whiteColor });
  page12.drawText(
    `the Principal countersigns. ${data.agreementDate || ''} is auto-populated by the signing platform on execution.`,
    { x: 70.5, y: 224, size: 7.5, font: fontRegular, color: textColor },
  );

  // ═══════════════════════════════════════════════════════════════
  // PAGE 13 (pages[12]): Schedule 1 — Service Particulars
  // ═══════════════════════════════════════════════════════════════
  // Principal details block:
  //   "Firm Name: [                    ]"   LINE x0=192.1 y0=603.7 x1=352.3 y1=613.7
  //   "ACN/ABN:    [                    ]"  LINE x0=192.1 y0=592.2 x1=355.6 y1=602.2
  //   "Address:      [                  ]"  LINE x0=192.1 y0=580.7 x1=353.4 y1=590.7
  //   "Email:           [               ]"  LINE x0=192.1 y0=569.2 x1=355.6 y1=579.2
  //   "Attention:    [                  ]"  LINE x0=192.1 y0=557.7 x1=350.6 y1=567.7

  const page13 = pages[12];
  const scFs = 9;
  const scLabelW = 68; // width of the label portion ("Firm Name: ")

  // Address wraps to max 2 lines (60-char input limit, 30 chars/line)
  const scAddrLines = data.partyAddress ? splitText(data.partyAddress, 30).slice(0, 2) : [];
  const scExtraAddrLines = Math.max(0, scAddrLines.length - 1);

  // When address wraps to 2 lines compress to 8px to keep all rows within the fixed cell
  // (top y≈605, bottom y≈556): L2=574, Email=566, Attention=558 — all above y=556 ✓
  const scAddrSpacing = scExtraAddrLines > 0 ? 8 : 11.5;

  // White-out entire principal block (fixed size — content stays within cell)
  page13.drawRectangle({ x: 191, y: 556, width: 170, height: 60, color: whiteColor });

  // Re-draw labels + filled values
  page13.drawText('Firm Name:', { x: 192.1, y: 605, size: scFs, font: fontRegular, color: textColor });
  page13.drawText(data.partyName || '', { x: 192.1 + scLabelW, y: 605, size: scFs, font: fontBold, color: textColor });

  page13.drawText('ACN/ABN:', { x: 192.1, y: 593.5, size: scFs, font: fontRegular, color: textColor });
  if (acnAbnLabel) {
    page13.drawText(acnAbnLabel, { x: 192.1 + scLabelW, y: 593.5, size: scFs, font: fontRegular, color: textColor });
  }

  page13.drawText('Address:', { x: 192.1, y: 582, size: scFs, font: fontRegular, color: textColor });
  scAddrLines.forEach((line, i) => {
    page13.drawText(line, {
      x: 192.1 + scLabelW, y: 582 - (i * scAddrSpacing), size: scFs, font: fontRegular, color: textColor,
    });
  });

  const scEmailY = 582 - (scAddrLines.length * scAddrSpacing);
  page13.drawText('Email:', { x: 192.1, y: scEmailY, size: scFs, font: fontRegular, color: textColor });
  if (data.partyEmail) {
    page13.drawText(data.partyEmail, { x: 192.1 + scLabelW, y: scEmailY, size: scFs, font: fontRegular, color: linkColor });
  }

  const scAttnY = scEmailY - scAddrSpacing;
  page13.drawText('Attention:', { x: 192.1, y: scAttnY, size: scFs, font: fontRegular, color: textColor });
  if (data.signatoryName) {
    page13.drawText(data.signatoryName, { x: 192.1 + scLabelW, y: scAttnY, size: scFs, font: fontRegular, color: textColor });
  }

  // 3. Serialize the modified PDF
  const filledBytes = await pdfDoc.save();

  // 4. Create blob URL
  const blob = new Blob([filledBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

/**
 * Generate the filled agreement PDF and return raw bytes (Uint8Array).
 * Used for uploading the signed PDF to the backend during onboarding.
 */
export async function fillAgreementPdfBytes(data: AgreementPdfData): Promise<Uint8Array> {
  // Reuse the same logic but capture the blob URL, fetch it, and return bytes
  const blobUrl = await fillAgreementPdf(data);
  const response = await fetch(blobUrl);
  const arrayBuffer = await response.arrayBuffer();
  URL.revokeObjectURL(blobUrl);
  return new Uint8Array(arrayBuffer);
}

/**
 * Split text into lines of maxLen characters, breaking at spaces.
 */
function splitText(text: string, maxLen: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    if (current.length + word.length + 1 > maxLen && current.length > 0) {
      lines.push(current.trim());
      current = word;
    } else {
      current += (current ? ' ' : '') + word;
    }
  }
  if (current) lines.push(current.trim());
  return lines;
}
